import { gs1Mod10Valid } from '../../expressions/gs1.js';
import { parseDigitalLinkUri, parseElementString } from './parse.js';
/**
 * Lecture d'un code scanné (ADR-146 addendum (6), widgets `scan_code` et `lot`) : une URI GS1 Digital
 * Link, une chaîne d'éléments (brute à séparateur GS ou parenthésée, identifiant de symbologie admis),
 * un GTIN seul (code EAN ou UPC lu sans AI) ou un SSCC seul. Fonction pure, sans réseau : une URI n'est
 * jamais résolue (R3). Même code au serveur et au navigateur ; la tablette en a un port rejoué par le jeu
 * `gs1-scan.cases.json`. Toutes les valeurs sont des chaînes (ERP-REF-001) ; une date GS1 devient une
 * date civile AAAA-MM-JJ (R1, ERP-DATE-001).
 */
/** Valeurs nommées qu'un scan peut remplir (`presentation.scan.fills`, inspection-template-body 1.9.0). */
export const SCAN_FILL_NAMES = ['gtin', 'lot', 'serial', 'sscc', 'expiry', 'best_before', 'production'];
/** AI GS1 de chaque valeur nommée. */
export const SCAN_FILL_AI = Object.freeze({ gtin: '01', lot: '10', serial: '21', sscc: '00', expiry: '17', best_before: '15', production: '11' });
/** Valeurs nommées qui sont des dates civiles. */
export const SCAN_DATE_FILLS = new Set(['expiry', 'best_before', 'production']);
/**
 * Date GS1 AAMMJJ en date civile AAAA-MM-JJ : siècle par la fenêtre glissante des spécifications
 * générales GS1 (§7.12) — de 49 ans avant à 50 ans après l'année de référence ; jour `00` = dernier jour
 * du mois. `null` pour une date impossible.
 */
export function gs1CivilDate(value, referenceYear) {
    const match = /^(\d{2})(\d{2})(\d{2})$/.exec(value);
    if (match === null)
        return null;
    const yy = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12)
        return null;
    const century = Math.floor(referenceYear / 100) * 100;
    let year = century + yy;
    const delta = yy - (referenceYear % 100);
    if (delta >= 51)
        year -= 100;
    else if (delta <= -50)
        year += 100;
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    if (day > lastDay)
        return null;
    const pad = (number, width) => String(number).padStart(width, '0');
    return `${pad(year, 4)}-${pad(month, 2)}-${pad(day === 0 ? lastDay : day, 2)}`;
}
const fail = (code, message, ai) => ({ ok: false, error: ai === undefined ? { code, message } : { code, message, ai } });
function named(kind, elements, referenceYear) {
    const values = {};
    for (const name of SCAN_FILL_NAMES) {
        const element = elements.find((candidate) => candidate.ai === SCAN_FILL_AI[name]);
        if (element === undefined)
            continue;
        if (SCAN_DATE_FILLS.has(name)) {
            const civil = gs1CivilDate(element.value, referenceYear);
            if (civil === null)
                return fail('GS1_VALUE_INVALID', `AI (${element.ai}) : la date n'existe pas.`, element.ai);
            values[name] = civil;
        }
        else {
            values[name] = element.value;
        }
    }
    return { ok: true, kind, elements, values };
}
/** Retire la fin de rafale d'une douchette (retour chariot, saut de ligne, tabulation) ; rien d'autre. */
export function scanText(raw) {
    return raw.replace(/[\r\n\t]+$/, '').replace(/^[\r\n\t ]+/, '');
}
/** Lit un code scanné ; refus nommé pour un code vide, illisible ou non conforme au dictionnaire GS1. */
export function analyzeScan(raw, options) {
    const input = scanText(raw);
    if (input.trim() === '')
        return fail('SCAN_EMPTY', 'Aucun code lu.');
    if (/^https?:\/\//i.test(input)) {
        const parsed = parseDigitalLinkUri(input);
        if (!parsed.ok)
            return { ok: false, error: parsed.error };
        return named('digital_link', [parsed.value.primary, ...parsed.value.qualifiers, ...parsed.value.attributes], options.referenceYear);
    }
    // Code EAN, UPC ou ITF lu sans AI : un GTIN seul, porté sur 14 chiffres (zéros de tête, jamais un nombre).
    if (/^\d+$/.test(input) && [8, 12, 13, 14].includes(input.length) && gs1Mod10Valid(input)) {
        return named('gtin', [{ ai: '01', value: input.padStart(14, '0') }], options.referenceYear);
    }
    if (/^\d{18}$/.test(input) && gs1Mod10Valid(input))
        return named('sscc', [{ ai: '00', value: input }], options.referenceYear);
    if (!/^(\]|\(|\^|\d)/.test(input))
        return fail('SCAN_UNREADABLE', 'Ce code n’est ni un code GS1 ni un code article.');
    const parsed = parseElementString(input);
    if (!parsed.ok)
        return { ok: false, error: parsed.error };
    return named('element_string', parsed.elements, options.referenceYear);
}
//# sourceMappingURL=scan.js.map